import { Component, type ErrorInfo, type ReactNode } from "react";
import { formatRuntimeError, reportRuntimeError, type RuntimeErrorRecord } from "@/runtime/runtimeErrors";

interface RuntimeErrorBoundaryProps { children: ReactNode }
interface RuntimeErrorBoundaryState { error: RuntimeErrorRecord | null }

export class RuntimeErrorBoundary extends Component<RuntimeErrorBoundaryProps, RuntimeErrorBoundaryState> {
  state: RuntimeErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): RuntimeErrorBoundaryState {
    const formatted = formatRuntimeError(error);
    return {
      error: {
        context: "React render error",
        message: formatted.message,
        details: formatted.details,
        report: `React render error: ${formatted.message}\n\n${formatted.details}`,
      },
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const enriched = Object.assign(error instanceof Error ? error : new Error(String(error)), { componentStack: info.componentStack });
    const record = reportRuntimeError(enriched, "React render error");
    if (record) this.setState({ error: record });
  }

  render() {
    return this.state.error ? <RuntimeErrorScreen record={this.state.error} /> : this.props.children;
  }
}

export function RuntimeErrorScreen({ record }: { record: RuntimeErrorRecord }) {
  return (
    <main className="runtime-error-screen" role="alert" data-testid="runtime-error">
      <div className="runtime-error-card">
        <span className="runtime-error-kicker">Rising Pixel / exception</span>
        <h1>Experience stopped</h1>
        <p>{record.message}</p>
        <RuntimeErrorDetails record={record} />
        <button type="button" onClick={() => window.location.reload()}>Reload</button>
      </div>
    </main>
  );
}

export function RuntimeErrorDetails({ record }: { record: RuntimeErrorRecord }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(record.report);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = record.report;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.append(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }
  };
  return (
    <>
      <details className="runtime-error-details" open>
        <summary>Technical details</summary>
        <pre>{record.details}</pre>
      </details>
      <button className="runtime-error-copy" type="button" onClick={() => void copy()}>Copy error details</button>
    </>
  );
}
